/**
 * Currency formatting utilities for BizOP.
 *
 * Default base currency is BDT (Bangladeshi Taka). The system is multi-currency-native:
 * any code can be set as `companyProfile.baseCurrency` for future SaaS use.
 *
 * Rendering rules:
 *   - Always use Intl.NumberFormat — handles locale-aware grouping, decimals, and
 *     numeral systems (Bengali numerals for `bn-BD` locale).
 *   - Symbol vs code: prefer symbol for the base currency in primary contexts,
 *     code (USD, EUR) for foreign currencies in finance/imports views.
 *   - Tabular numerals applied via CSS — see ui-kit tokens.
 */

import { decimalsFor, toMajorUnits } from "./money.js";

/** Code of the application's default base currency. */
export const DEFAULT_BASE_CURRENCY = "BDT";

/** Currencies BizOP commonly transacts in. Used for selectors. */
export const COMMON_CURRENCIES: ReadonlyArray<{
  code: string;
  name: string;
  nameBn?: string;
  symbol: string;
}> = [
  { code: "BDT", name: "Bangladeshi Taka", nameBn: "বাংলাদেশী টাকা", symbol: "৳" },
  { code: "USD", name: "US Dollar", nameBn: "মার্কিন ডলার", symbol: "$" },
  { code: "EUR", name: "Euro", nameBn: "ইউরো", symbol: "€" },
  { code: "GBP", name: "British Pound", nameBn: "ব্রিটিশ পাউন্ড", symbol: "£" },
  { code: "CNY", name: "Chinese Yuan", nameBn: "চীনা ইউয়ান", symbol: "¥" },
  { code: "JPY", name: "Japanese Yen", nameBn: "জাপানি ইয়েন", symbol: "¥" },
  { code: "INR", name: "Indian Rupee", nameBn: "ভারতীয় রুপি", symbol: "₹" },
  { code: "AED", name: "UAE Dirham", nameBn: "সংযুক্ত আরব আমিরাত দিরহাম", symbol: "د.إ" },
  { code: "SAR", name: "Saudi Riyal", nameBn: "সৌদি রিয়াল", symbol: "ر.س" },
];

const CURRENCY_BY_CODE: Map<string, (typeof COMMON_CURRENCIES)[number]> = new Map(
  COMMON_CURRENCIES.map((c) => [c.code, c]),
);

export function getCurrencyMeta(code: string) {
  return CURRENCY_BY_CODE.get(code.toUpperCase());
}

export type FormatMoneyOptions = {
  /** Locale for formatting. Defaults to en-BD. Use bn-BD for Bengali numerals. */
  locale?: string;
  /** "symbol" (default), "code", "name", or "narrowSymbol". */
  display?: "symbol" | "code" | "name" | "narrowSymbol" | "none";
  /** Override decimal display. Default: currency's natural decimals. */
  fractionDigits?: number;
};

/**
 * Format a BigInt-cents amount as a localized currency string.
 * Examples:
 *   formatMoney(125000n, "BDT")              → "৳1,250.00"
 *   formatMoney(125000n, "BDT", { locale: "bn-BD" }) → "৳১,২৫০.০০"
 *   formatMoney(99500n, "USD", { display: "code" }) → "USD 995.00"
 */
export function formatMoney(
  amountMinor: bigint,
  currencyCode: string,
  options: FormatMoneyOptions = {},
): string {
  const locale = options.locale ?? "en-BD";
  const code = currencyCode.toUpperCase();
  const decimals = options.fractionDigits ?? decimalsFor(code);
  const major = toMajorUnits(amountMinor, code);

  if (options.display === "none") {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(major);
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: options.display ?? "symbol",
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(major);
  } catch {
    // Fallback if locale/currency combo is unsupported by the runtime.
    const meta = getCurrencyMeta(code);
    const symbol = meta?.symbol ?? code;
    const number = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(major);
    return options.display === "code" ? `${code} ${number}` : `${symbol}${number}`;
  }
}

/**
 * Compact format with K/M/B prefixes.
 *
 *   500          → "BDT 500" (or "৳500" with display: "symbol")
 *   12_300       → "BDT 12K"
 *   234_500      → "BDT 235K"
 *   3_211_593    → "BDT 3.2M"
 *   1_500_000_000 → "BDT 1.5B"
 *
 * Default locale is en-US (clean K/M/B), not en-BD (which produces
 * Indian-numbering "L" / "Cr"). Pass `locale: "en-IN"` if you want lakhs.
 */
export function formatMoneyCompact(
  amountMinor: bigint,
  currencyCode: string,
  options: { locale?: string; display?: "symbol" | "code" } = {},
): string {
  const locale = options.locale ?? "en-US";
  const code = currencyCode.toUpperCase();
  const major = toMajorUnits(amountMinor, code);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      currencyDisplay: options.display ?? "code",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(major);
  } catch {
    return formatMoney(amountMinor, code, options);
  }
}

/**
 * Smart money formatter — picks full vs compact based on magnitude.
 *
 *   < 10,000   : full format ("BDT 9,999.50")
 *   >= 10,000  : compact ("BDT 12K", "BDT 234K", "BDT 3.2M", "BDT 1.5B")
 *
 * Designed for KPI cards and tight grid columns where 7-digit values
 * cause overflow. Negative numbers preserve their sign through both
 * branches.
 */
export function formatMoneySmart(
  amountMinor: bigint,
  currencyCode: string,
  options: FormatMoneyOptions = {},
): string {
  const major = Math.abs(toMajorUnits(amountMinor, currencyCode));
  if (major < 10_000) {
    return formatMoney(amountMinor, currencyCode, options);
  }
  // Compact only supports symbol or code. Map narrowSymbol/name/none → code
  // so we never accidentally show two currency symbols.
  const display: "symbol" | "code" =
    options.display === "symbol" ? "symbol" : "code";
  return formatMoneyCompact(amountMinor, currencyCode, {
    locale: options.locale,
    display,
  });
}
